/**
 * OneTimePasswordDialog — shows a server-generated temporary password that
 * the operator is expected to copy and relay to the target user.
 *
 * UX contract:
 *   - The password is displayed exactly once. Re-opening the dialog later
 *     won't show it again — the API never re-emits the raw value.
 *   - "Copiar" button uses the Clipboard API when available. Falls back to a
 *     textarea+select on older browsers.
 */

import { useRef, useState } from 'react';
import { Copy, Check } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

interface Props {
  open: boolean;
  email: string;
  temporaryPassword: string;
  onClose: () => void;
}

export function OneTimePasswordDialog({ open, email, temporaryPassword, onClose }: Props) {
  const [copied, setCopied] = useState(false);
  const preRef = useRef<HTMLPreElement>(null);

  async function copy() {
    try {
      if (typeof navigator !== 'undefined' && navigator.clipboard) {
        await navigator.clipboard.writeText(temporaryPassword);
      } else if (preRef.current) {
        const range = document.createRange();
        range.selectNodeContents(preRef.current);
        const sel = window.getSelection();
        sel?.removeAllRanges();
        sel?.addRange(range);
        document.execCommand('copy');
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* ignore — user can copy manually */
    }
  }

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Senha temporária gerada</DialogTitle>
          <DialogDescription>
            Esta senha será mostrada apenas uma vez. Copie-a e envie ao usuário{' '}
            <span className="font-mono">{email}</span>. Ele será forçado a trocá-la no primeiro
            login.
          </DialogDescription>
        </DialogHeader>
        <pre
          ref={preRef}
          data-testid="temp-password-value"
          className="break-all rounded-md border bg-muted px-4 py-3 font-mono text-sm"
        >
          {temporaryPassword}
        </pre>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Fechar
          </Button>
          <Button onClick={copy} aria-label="Copiar senha">
            {copied ? <Check aria-hidden /> : <Copy aria-hidden />}
            {copied ? 'Copiado' : 'Copiar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
